import { createApi } from '@reduxjs/toolkit/query/react';
import type {
  CustomerDto,
  CustomerListDto,
  CustomerListParams,
  CreateCustomerRequest,
} from '@/types';
import { baseQueryWithRetry } from './baseQuery';
import { TAG_CUSTOMER } from '@/store/tagTypes';

export const customersApi = createApi({
  reducerPath: 'customersApi',
  baseQuery: baseQueryWithRetry,
  tagTypes: [TAG_CUSTOMER],
  endpoints: (builder) => ({
    listCustomers: builder.query<CustomerListDto, CustomerListParams | void>({
      query: (params) => ({
        url: '/customers',
        params: params
          ? {
              page: params.page?.toString(),
              pageSize: params.pageSize?.toString(),
              search: params.search,
            }
          : undefined,
      }),
      providesTags: (result) =>
        result?.items
          ? [
              ...result.items.map(({ id }) => ({ type: TAG_CUSTOMER, id })),
              { type: TAG_CUSTOMER, id: 'LIST' },
            ]
          : [{ type: TAG_CUSTOMER, id: 'LIST' }],
    }),

    getCustomer: builder.query<CustomerDto, string>({
      query: (id) => `/customers/${id}`,
      providesTags: (_result, _error, id) => [{ type: TAG_CUSTOMER, id }],
    }),

    createCustomer: builder.mutation<CustomerDto, CreateCustomerRequest>({
      query: (body) => ({
        url: '/customers',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: TAG_CUSTOMER, id: 'LIST' }],
    }),

    updateCustomer: builder.mutation<void, { id: string } & CreateCustomerRequest>({
      query: ({ id, ...body }) => ({
        url: `/customers/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: TAG_CUSTOMER, id },
        { type: TAG_CUSTOMER, id: 'LIST' },
      ],
    }),

    deleteCustomer: builder.mutation<void, string>({
      query: (id) => ({
        url: `/customers/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: TAG_CUSTOMER, id },
        { type: TAG_CUSTOMER, id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useListCustomersQuery,
  useGetCustomerQuery,
  useCreateCustomerMutation,
  useUpdateCustomerMutation,
  useDeleteCustomerMutation,
} = customersApi;
